import type {LogLevel} from '@significo/pulse';
import {Language} from '@/ts/types/component/localization.type';
import {parseLogLevel} from '@/ts/utils/log-level-parser';
import type {ZitadelGetTokenResponse, TokenData} from '@/ts/types/dto/zitadel-dto';
import {detectBrowserLanguage} from '@/ts/utils/browser-language';

/** Typed wrapper around localStorage for auth tokens and language */
export class PersistentStorage {
  public get logLevel(): LogLevel | null {
    const stored = localStorage.getItem('log-level');
    if (stored === null) {
      return null;
    }
    return parseLogLevel(stored);
  }

  public clear(): void {
    // Reset language to browser default on logout so new users get their preferred language
    this.language = detectBrowserLanguage();
  }

  public get language(): Language | null {
    const stored = localStorage.getItem('lang');
    if (stored && Object.values(Language).includes(stored as Language)) {
      return stored as Language;
    }
    return null;
  }

  public set language(value: Language | null) {
    if (value) {
      localStorage.setItem('lang', value);
    } else {
      localStorage.removeItem('lang');
    }
  }

  public get auth(): ZitadelGetTokenResponse | null {
    const auth = localStorage.getItem('auth');
    if (auth) {
      return JSON.parse(auth) as ZitadelGetTokenResponse;
    }
    return null;
  }

  get authExpired(): boolean {
    const auth = localStorage.getItem('auth');
    if (!auth) {
      return true;
    }
    const data = JSON.parse(auth) as TokenData;
    const extraTime = 60; // 60 seconds
    return Date.now() - data.setOn > (data.expires_in - extraTime) * 1000;
  }

  public set auth(data: ZitadelGetTokenResponse | null) {
    if (data) {
      const newData: TokenData = {...data, setOn: Date.now()};
      localStorage.setItem('auth', JSON.stringify(newData));
      localStorage.removeItem('codeVerifier');
      localStorage.removeItem('signUpEmail');
    } else {
      localStorage.removeItem('auth');
      localStorage.removeItem('codeVerifier');
    }
  }

  // The email used at sign-up, kept until the first login so the verify-email
  // page can prefill Zitadel's login via login_hint (the verification link
  // itself does not carry the email).
  public get signUpEmail(): string | null {
    return localStorage.getItem('signUpEmail') ?? null;
  }

  public set signUpEmail(email: string | null) {
    if (email) {
      localStorage.setItem('signUpEmail', email);
    } else {
      localStorage.removeItem('signUpEmail');
    }
  }

  public get codeVerifier(): string | null {
    return localStorage.getItem('codeVerifier') ?? null;
  }

  public set codeVerifier(data: string | null) {
    if (data) {
      localStorage.setItem('codeVerifier', data);
    } else {
      localStorage.removeItem('codeVerifier');
    }
  }
}
